import { StyleSheet, css } from 'aphrodite';
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import { SimpleTooltip, TooltipProvider } from '../../../../../../web/components/Tooltip';
import { Toggle } from '../../../../../../web/components/Toggle';
import { InfoIcon } from '../../../../../../web/components/svgs/icons/InfoIcon';
import { grayIconFill } from '../../../../../../web/styles/colors';
import { FormFieldWrapper } from '../FormFieldWrapper';

interface ShowSurveyNameToggleProps<TFieldValues extends FieldValues> {
	control: Control<TFieldValues>;
	description: string;
	disabled?: boolean;
	id: string;
	name: FieldPath<TFieldValues>;
	/**
	 * When set, the toggle is locked (disabled) and an info icon with this tooltip explains why it
	 * cannot be changed. Used when there is no custom survey header, in which case the name is always
	 * rendered by the default header and the toggle has no effect.
	 */
	lockedReason?: string;
}

export const ShowSurveyNameToggle = <TFieldValues extends FieldValues>({
	control,
	description,
	disabled = false,
	id,
	name,
	lockedReason,
}: ShowSurveyNameToggleProps<TFieldValues>) => {
	const isLocked = !!lockedReason;

	return (
		<FormFieldWrapper<TFieldValues>
			name={name}
			control={control}
			title='Show Survey Name'
			description={description}
			titleAdornment={
				isLocked ? (
					<TooltipProvider>
						<SimpleTooltip title={lockedReason} styles={[styles.tooltip]}>
							<span className={css(styles.infoTrigger)} aria-label='More information' tabIndex={0}>
								<InfoIcon className={css(styles.infoIcon)} fillColor={grayIconFill} />
							</span>
						</SimpleTooltip>
					</TooltipProvider>
				) : undefined
			}
		>
			{({ value, onChange }) => {
				const isOn = value === true;

				return (
					<Toggle
						id={id}
						isOn={isOn}
						disabled={disabled || isLocked}
						onToggleCheckChanged={() => onChange(!isOn)}
						uncheckedColor={grayIconFill}
					/>
				);
			}}
		</FormFieldWrapper>
	);
};

const styles = StyleSheet.create({
	infoTrigger: {
		display: 'inline-flex',
		alignItems: 'center',
		cursor: 'help',
	},
	infoIcon: {
		display: 'block',
	},
	tooltip: {
		maxWidth: 260,
		whiteSpace: 'normal',
		lineHeight: 1.4,
	},
});
